import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import EntryFormHeader from '../../components/header/EntryFormHeader';
import { RootState } from '../../redux/store';
import { setField } from '../../redux/entryFormSlice';
import { NEW_VERIFICATION } from '../../graphql/queries';
import { SUBMIT_FORM_INPUT, UPLOAD_IMAGE_FILE_DOCUMENT, UPLOAD_PDF_DOCUMENT, CORPORATE_NUMBER_VERIFICATION } from '../../graphql/mutations';
import Loading from '../../components/common/Loading';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
interface LegalPersonalEntryFormGuideType {
}

declare const Trustdock: any;

type DetectedFile = {
  file: File;
  fileType: 'pdf' | 'jpeg' | 'png' | 'gif'
}

const dragStyles = {
  dragging: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderColor: '#3b82f6',
    borderStyle: 'dashed'
  }
};

const LegalPersonalEntryFormGuide: React.FC<LegalPersonalEntryFormGuideType> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const entryFormInfo = useSelector((state: RootState) => state.entryForm);

  const { loading, error, data } = useQuery(NEW_VERIFICATION);
  
  const [submitFormInput] = useMutation(SUBMIT_FORM_INPUT);
  const [corporateNumberVerification] = useMutation(CORPORATE_NUMBER_VERIFICATION);
  const [uploadImageFileDocument] = useMutation(UPLOAD_IMAGE_FILE_DOCUMENT);
  const [uploadPDFDocument] = useMutation(UPLOAD_PDF_DOCUMENT);

  const [legalPersonPlanId, setLegalPersonPlanId] = useState<string>('');
  const [refererPlanId, setRefererPlanId] = useState<string>('');
  const [corporatePublicId, setCorporatePublicId] = useState<string>('');

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFileType, setSelectedFileType] = useState<string>('');
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [trustdock, setTrustdock] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadedTrustdock, setIsLoadedTrustdock] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const isPersonalVerificationDisabled = useMemo(
    () => !legalPersonPlanId || ! refererPlanId || !isLoadedTrustdock,
    [
      legalPersonPlanId,
      refererPlanId,
      isLoadedTrustdock
    ]
  );

  const scrollToStep2 = () => {
    const element = document.getElementById("step2");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (trustdock) {
      //// Processing when an event is received
      // Describe the process when identity verification documents have been submitted
      // For example, forcing a transition to the next page for users who do not close the modal after submission, etc.
      trustdock.on('documentsubmitted', documentsubmittedAction)
      // Describes the process when identity verification documents have been submitted and the modal is closed
      trustdock.on('completed', () => {
        trustdock.isCompleted = true;
        if (corporatePublicId) {
          completedAction();
        }
      })
      // Describes what to do if the modal is closed without submitting identification documents
      trustdock.on('canceled', canceledAction)
    }
  }, [trustdock]);

  useEffect(() => {
    if (corporatePublicId && trustdock?.isCompleted) {
      completedAction();
      trustdock.isCompleted = false;
    }
  }, [corporatePublicId, trustdock?.isCompleted]);

  useEffect(() => {
    if (!legalPersonPlanId || !refererPlanId) return;
    const script = document.createElement("script");
    script.src = process.env.REACT_APP_TRUSTDOCK_HELPER_URL || 'https://helper.test.trustdock.io/v2/verification_helper.js';
    script.async = true;
  
    script.onload = () => {
      if (isLoadedTrustdock) removeTrustdock();
  
      const trustdockInstance = new Trustdock({
        publicId: entryFormInfo.publicId,
        plans: [{ id: refererPlanId }],
        openerSelector: ".opener",
      });

      setTrustdock(trustdockInstance);
  
      trustdockInstance.on("loaded", () => {
        console.log("Trustdock is loaded");
        setIsLoadedTrustdock(true);
      });
  
      trustdockInstance.on("verified", () => {
        console.log("Trustdock verification is verified.");
        handleVerification();
      });

      trustdockInstance.on("approved", () => {
        console.log("Trustdock verification is approved.");
        handleVerification();
      });

    };
    document.body.appendChild(script);
  
    return () => {
      document.body.removeChild(script);
    }
  }, [legalPersonPlanId, refererPlanId, entryFormInfo]);

  useEffect(() => {
    if((entryFormInfo.entryFormStep !== 'guide') && (entryFormInfo.entryFormStep !== 'ekyc') && (entryFormInfo.entryFormStep !== 'complete')) {
      dispatch(setField({field: 'entryFormStep', value: 'entry'}))
      navigate('/form/legalPersonal')
    }
  }, [entryFormInfo.entryFormStep])
  

  const handleVerification = async () => {
    if (!legalPersonPlanId || !refererPlanId) return;
  
    let uploaderAvailable = true;
  
    try {
      // Case 1: CORPORATE_NUMBER verification
      if (legalPersonPlanId === entryFormInfo.planId_CORPORATE_NUMBER) {
        const { data } = await corporateNumberVerification({
          variables: { externalId: entryFormInfo.externalId },
        });
  
        setCorporatePublicId(data?.corporateNumberVerification ?? "");
      }
  
      // Case 2: CORPORATE_VERIFICATION_CERTIFICATE_OF_AL_REGISTERED_MATTERS
      if (
        legalPersonPlanId ===
        entryFormInfo.planId_CORPORATE_VERIFICATION_CERTIFICATE_OF_AL_REGISTERED_MATTERS
      ) {
        if (!selectedFiles || selectedFiles.length === 0) {
          throw new Error("No files selected.");
        }
  
        const firstFile = selectedFiles[0];
  
        if (firstFile.type.includes("image")) {
          const { data } = await uploadImageFileDocument({
            variables: {
              externalId: entryFormInfo.externalId,
              file1: selectedFiles[0],
              file2: selectedFiles[1],
              file3: selectedFiles[2],
              file4: selectedFiles[3],
              file5: selectedFiles[4],
              file6: selectedFiles[5],
              file7: selectedFiles[6],
              file8: selectedFiles[7],
              file9: selectedFiles[8],
              file10: selectedFiles[9],
            },
          });

          for (let i = 0; i < 10; i++) {
            const message = data?.uploadImageFileDocument?.[i]?.message;
            if (message) throw new Error("Image upload failed.");
          }
          const publicId = data?.uploadImageFileDocument?.[0]?.publicId;
          if (!publicId) throw new Error("Image upload failed.");
          setCorporatePublicId(publicId);
        } else if (firstFile.type.includes("pdf")) {
          const { data } = await uploadPDFDocument({
            variables: {
              externalId: entryFormInfo.externalId,
              pdf: firstFile,
            },
          });

          const message = data?.uploadPDFDocument?.message;
          if (message) throw new Error("PDF upload failed.");
          const publicId = data?.uploadPDFDocument?.publicId;
          if (!publicId) throw new Error("PDF upload failed.");
          setCorporatePublicId(publicId);
        } else {
          throw new Error("Unsupported file type.");
        }
      }
    } catch (error) {
      alert('ファイルをアップロードできません。ファイルが壊れているか、拡張子が実態と異なる可能性があります。')
      uploaderAvailable = false;
      setIsLoading(false);
    }
  
    // Final Trustdock action
    if (trustdock && uploaderAvailable) {
      trustdock.open();
    }
  };  

  const validateFiles = (files: FileList | null) =>{
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      fileArray.map(async item => {
        const fileData = await handleFile(item)
        if(fileData !== null) {
          if(selectedFiles.length === 0) {
            if(fileData.fileType === 'pdf') setSelectedFileType('pdf')
            else if(fileData.fileType === 'jpeg' || fileData.fileType === 'png' || fileData.fileType === 'gif') setSelectedFileType('image');

            setSelectedFiles([item])
          } 

          else {
            if(selectedFileType === 'pdf') alert('ファイルをアップロードできません。PDFは1ファイル（10ページ以下）のみアップロード可能です。')
            else if(selectedFileType === 'image') {
              if(fileData.fileType === 'pdf') 
                alert('画像ファイルとPDFの組み合わせアップロードはできません。1つのPDF（10ページ以下）か最大10ファイルの画像ファイルをアップロード可能です。')
              else if (selectedFiles.length >= 10) alert('ファイルをアップロードできません。画像ファイルは最大10ファイルのみアップロード可能です。）');
              else setSelectedFiles(prev => [...prev, item]);
            }
          }
        }
      });
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    validateFiles(files);
  }

  const handleFile = async (item: File): Promise<DetectedFile | null> => {
    if (selectedFiles.length >= 10) {
      alert('アップロードできるファイルは10個までです。');
      return null;
    }

    // ファイルタイプチェック
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(item.type)) {
      alert('PDFまたは画像ファイル（JPG、PNG、GIF）のみアップロード可能です。');
      return null;
    }
  
    // サイズチェック
    if (item.size > 10 * 1024 * 1024) { // 10MB
      alert('ファイルサイズは10MB以内にしてください。');
      return null;
    }
  
    const buffer = await item.slice(0, 4).arrayBuffer();
    const bytes = new Uint8Array(buffer);
  
    // Check file signatures (magic numbers)
    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
      // PDF: %PDF
      const arrayBuffer = await item.arrayBuffer();
      const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
      const pageCount = pdf.numPages;
      console.log("page count:", pageCount);
  
      if(pageCount > 10) {
        alert('これ以上ファイルをアップロードできません。PDFは1ファイル（10ページ以下）のみアップロード可能です。')
        return null;
      }
  
      return { file: item, fileType: 'pdf' };
    }
    
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
      // PNG
      console.log('Valid PNG file');
      return { file: item, fileType: 'png' };
    }
  
    if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
      // JPEG
      console.log('Valid JPEG file');
      return { file: item, fileType: 'jpeg' };
    }
  
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
      // GIF
      console.log('Valid GIF file');
      return { file: item, fileType: 'gif' };
    }
  
    alert('ファイル形式が正しくありません。PDFまたは画像（JPG、PNG、GIF）のみ許可されています。');
    return null;
  };

  const handeRemoveSelectedFile = (index: number) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    validateFiles(e.dataTransfer.files)
  };

  const documentsubmittedAction = () => {
    console.log('received the documentsubmitted event');
    setIsLoading(false);
    setIsSubmitted(true);
  }
  
  const canceledAction = () => {
    console.log('received the canceled event');
    setIsLoading(false);
    window.alert('Trustdock Uploader is failed');
  }
  const completedAction = async() => {
    console.log("Trustdock submit is completed.")
    try {
      const submitFormResult = await submitFormInput({
        variables: {
          externalId: entryFormInfo.externalId,
          corporatePublicId: corporatePublicId,
          personalPublicId: entryFormInfo.publicId,
          planId: legalPersonPlanId,
          corporateName: entryFormInfo.companyName,
          corporateNumber: entryFormInfo.corporateNumber,
          corporatePostalCode: entryFormInfo.companyPostalCode,
          corporatePrefecture: entryFormInfo.companyPrefecture,
          corporateCity: entryFormInfo.companyCity,
          corporateStreet: entryFormInfo.companyStreetAddress,
          corporateBuilding: entryFormInfo.companyBuilding || '',
          representativeLastName: entryFormInfo.representativeLastName,
          representativeFirstName: entryFormInfo.representativeFirstName,
          personalLastName: entryFormInfo.lastName,
          personalFirstName: entryFormInfo.firstName,
          personalLastNameKana: entryFormInfo.lastNameKa,
          personalFirstNameKana: entryFormInfo.firstNameKa,
          personalBirthday: entryFormInfo.birthday,
          personalPostalCode: entryFormInfo.postalCode,
          personalPrefecture: entryFormInfo.prefecture,
          personalCity: entryFormInfo.city,
          personalStreet: entryFormInfo.address,
          personalBuilding: entryFormInfo.building || '',
          personalGender: entryFormInfo.gender,
          personalPhone: entryFormInfo.phoneNum,
          personalEmail: entryFormInfo.email
        }
      });
      
      dispatch(setField({field: 'submissionDate', value: submitFormResult.data.submitFormInput}));
      if(entryFormInfo.selectedRole == "attorney") {
        dispatch(setField({field: 'entryFormStep', value: 'ekyc'}))
        navigate('/form/legalPersonal/ekyc');
      } else {
        dispatch(setField({field: 'entryFormStep', value: 'complete'}))
        navigate('/form/legalPersonal/complete');
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  const removeTrustdock = () => {
    // Delete registered listeners with `trustdock.off`
    // Note that an anonymous function cannot be specified as a target for deletion, so it cannot be deleted.
    trustdock.off('documentsubmitted', documentsubmittedAction)
    trustdock.off('completed', completedAction)
    trustdock.off('canceled', canceledAction)
    // Delete generated DOM elements
    trustdock.remove()
    // It is possible to delete the file and use it again in another process by `new Trustdock()`.
  }

  if(data) {
    dispatch(setField({field: 'publicId', value: data.newVerification.publicId}));
    dispatch(setField({field: 'externalId', value: data.newVerification.externalId}));
    dispatch(setField({field: 'planId_E_KYC_HO', value: data.newVerification.plans.find((item: any) => item.name === 'E_KYC_HO').id}));
    dispatch(setField({field: 'planId_CORPORATE_NUMBER', value: data.newVerification.plans.find((item: any) => item.name === 'CORPORATE_NUMBER').id}));
    dispatch(setField({field: 'planId_PUBLIC_PERSONAL_AUTHENTICATION', value: data.newVerification.plans.find((item: any) => item.name === 'PUBLIC_PERSONAL_AUTHENTICATION').id}));
    dispatch(setField({field: 'planId_CORPORATE_VERIFICATION_CERTIFICATE_OF_AL_REGISTERED_MATTERS', value: data.newVerification.plans.find((item: any) => item.name === 'CORPORATE_VERIFICATION_CERTIFICATE_OF_AL_REGISTERED_MATTERS').id}));
    dispatch(setField({field: 'planId_SUPPLEMENTARY_DOCUMENT_VERIFICATION_SERVICE_LETTER_OF_ATTORNEY', value: data.newVerification.plans.find((item: any) => item.name === 'SUPPLEMENTARY_DOCUMENT_VERIFICATION_SERVICE_LETTER_OF_ATTORNEY').id}));
  }

  if (loading) return <Loading />;
  if (error) return <p className="text-center mt-4 text-red-500">Error: {error.message}</p>;

  return (
    <>
      <div className="bg-gray-50">
        <EntryFormHeader currentStep={3} />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold mb-6">法人確認の手順</h1>
          <p className="text-gray-600 mb-8">
            法人確認は以下の2つのステップで行います。それぞれの手順に従って進めてください。
          </p>

          <div className="guide-section mb-8">
            <h2 className="text-lg font-semibold mb-4">確認方法の選択について</h2>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• 法人確認方法と本人確認方法の両方を選択する必要があります</li>
              <li>• 登記事項証明書での確認を選択した場合は、ファイルのアップロードが必要です</li>
              <li>• 一度選択した方法は後から変更することができます</li>
              <li>• 選択した方法によって必要な準備物が異なります</li>
            </ul>
          </div>

          <div className="step-section">
            <div className="step-header">
              <div className="step-number">1</div>
              <h2 className="text-xl font-semibold">法人確認方法の選択</h2>
            </div>

            <div className="space-y-4">
              <div className={`verification-method ${legalPersonPlanId === entryFormInfo.planId_CORPORATE_NUMBER ? 'selected' : ''}`}>
                <input
                  type="radio"
                  id="corporateNumber"
                  name="corporateMethod"
                  value="number"
                  className="method-radio"
                  required
                />
                <label
                  htmlFor="corporateNumber"
                  className="cursor-pointer block"
                  onClick={() => setLegalPersonPlanId(entryFormInfo.planId_CORPORATE_NUMBER)}
                >
                  <div className="flex items-center mb-4">
                    <div className="radio-circle mr-3">
                      <div className="radio-dot"></div>
                    </div>
                    <h3 className="text-lg font-semibold">法人番号での確認</h3>
                  </div>

                  <div className="ml-9">
                    <p className="text-gray-600 mb-4">
                      国税庁が指定した13桁の法人番号を使用して法人確認を行います。
                    </p>

                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold mb-2">メリット</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        <li>その場で即時確認が可能</li>
                        <li>書類の郵送が不要</li>
                        <li>手続きが簡単</li>
                      </ul>
                    </div>

                    <div className="notice-box">
                      <h4 className="font-semibold mb-2">注意事項</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>法人番号は必ず確認の上で入力してください</li>
                        <li>入力ミスがあった場合は再度入力が必要です</li>
                      </ul>
                    </div>

                  </div>
                </label>
              </div>

              <div className={`verification-method ${legalPersonPlanId === entryFormInfo.planId_CORPORATE_VERIFICATION_CERTIFICATE_OF_AL_REGISTERED_MATTERS ? 'selected' : ''}`}>
                <input
                  type="radio"
                  id="corporateRegistry"
                  name="corporateMethod"
                  value="registry"
                  className="method-radio"
                  required
                />
                <label
                  htmlFor="corporateRegistry"
                  className="cursor-pointer block"
                  onClick={() => setLegalPersonPlanId(entryFormInfo.planId_CORPORATE_VERIFICATION_CERTIFICATE_OF_AL_REGISTERED_MATTERS)}
                >
                  <div className="flex items-center mb-4">
                    <div className="radio-circle mr-3">
                      <div className="radio-dot"></div>
                    </div>
                    <h3 className="text-lg font-semibold">登記事項証明書での確認</h3>
                  </div>

                  <div className="ml-9">

                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold mb-2">必要書類</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        <li>履歴事項全部証明書 または 現在事項全部証明書（発行から3ヶ月以内）</li>
                        <li>代表者もしくは担当者の本人確認書類（次のステップで選択）</li>
                      </ul>
                    </div>

                    <div className="mt-6">
                      <h4 className="font-semibold mb-2">登記事項証明書のアップロード</h4>
                      <label htmlFor="fileInput" className="block">
                        <div 
                          className="file-drop-zone" 
                          id="dropZone"
                          style={isDragging ? dragStyles.dragging : {}}
                          onDragEnter={handleDragEnter}
                          onDragLeave={handleDragLeave}
                          onDragOver={handleDragOver}
                          onDrop={handleDrop}
                        >
                          <input
                            type="file"
                            id="fileInput"
                            className="file-input"
                            accept=".pdf,.jpg,.jpeg,.png,.gif"
                            onChange={handleFileChange}
                            multiple
                          />
                          <div className="file-drop-content">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="mt-2 text-sm text-gray-600">
                              クリックまたはファイルをドラッグ＆ドロップ
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              PDF、JPG、JPEG、PNG、GIF形式のファイルをアップロードできます
                            </p>
                          </div>
                        </div>
                      </label>
                      <div
                        id="filePreview"
                        className={`${selectedFiles.length === 0 ? 'hidden' : ''}`}
                      >
                        {selectedFiles.map((item, index) => {
                          const fileSize = (item.size / (1024 * 1024)).toFixed(2);

                          return (
                            <div key={index}>
                              <div className="file-preview">
                                <svg className="file-preview-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <div className="file-preview-info">
                                  <div className="font-medium">{item.name}</div>
                                  <div className="file-size">{fileSize} MB</div>
                                </div>
                                <div
                                  className="file-preview-remove"
                                  onClick={() => handeRemoveSelectedFile(index)}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    <div className="notice-box mt-4">
                      <h4 className="font-semibold mb-2">アップロード時の注意点</h4>
                      <ul className="space-y-1 text-sm">
                        <li>• 有効期限内（発行から3ヶ月以内）の証明書をアップロードしてください</li>
                        <li>• 全てのページが鮮明に読み取れる必要があります</li>
                      </ul>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <div className="step-section">
            <div className="step-header">
              <div className="step-number">2</div>
              <h2 className="text-xl font-semibold">本人確認方法の選択</h2>
            </div>

            <div className="space-y-4">
              <div className={`verification-method ${refererPlanId === entryFormInfo.planId_E_KYC_HO ? 'selected' : ''}`}>
                <input
                  type="radio"
                  id="ekyc"
                  name="personalMethod"
                  value="ekyc"
                  className="method-radio"
                  required
                />
                <label
                  htmlFor="ekyc"
                  className="cursor-pointer block"
                  onClick={() => setRefererPlanId(entryFormInfo.planId_E_KYC_HO)}
                >
                  <div className="flex items-center mb-4">
                      <div className="radio-circle mr-3">
                          <div className="radio-dot"></div>
                      </div>
                      <h3 className="text-lg font-semibold">eKYC本人確認</h3>
                  </div>

                  <div className="ml-9">
                    <p className="text-gray-600 mb-4">
                      スマートフォンなどのカメラを使用して、その場で本人確認書類と顔写真を撮影する方法です。
                    </p>

                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold mb-2">準備するもの</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        <li>カメラ付きのスマートフォンまたはパソコン</li>
                        <li>本人確認書類（下記のいずれか）</li>
                        <li>十分な明るさのある環境</li>
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold mb-2">利用可能な本人確認書類</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        <li>運転免許証（有効期限内のもの）</li>
                        <li>マイナンバーカード（有効期限内のもの）</li>
                        <li>在留カード（有効期限内のもの）※外国籍の方</li>
                        <li>特別永住者証明書（有効期限内のもの）</li>
                      </ul>
                    </div>

                    <div className="notice-box">
                      <h4 className="font-semibold mb-2">確認時の注意点</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>本人確認書類は必ず原本をご用意ください</li>
                        <li>コピーや写真は受け付けできません</li>
                        <li>有効期限が切れている書類は使用できません</li>
                      </ul>
                    </div>
                  </div>
                </label>
              </div>

              <div className={`verification-method ${refererPlanId === entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION ? 'selected' : ''}`}>
                <input
                  type="radio"
                  id="jpki"
                  name="personalMethod"
                  value="jpki"
                  className="method-radio"
                  required
                />
                <label
                  htmlFor="jpki"
                  className="cursor-pointer block"
                  onClick={() => setRefererPlanId(entryFormInfo.planId_PUBLIC_PERSONAL_AUTHENTICATION)}
                >
                  <div className="flex items-center mb-4">
                    <div className="radio-circle mr-3">
                      <div className="radio-dot"></div>
                    </div>
                    <h3 className="text-lg font-semibold">マイナンバーカードによる公的個人認証</h3>
                  </div>

                  <div className="ml-9">
                    <p className="text-gray-600 mb-4">
                      マイナンバーカードのICチップに格納された電子証明書を利用して本人確認を行う方法です。
                    </p>

                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold mb-2">準備するもの</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        <li>マイナンバーカード（有効期限内のもの）</li>
                        <li> マイナンバーカード対応スマートフォン</li>
                        <li>利用者証明用電子証明書のパスワード（6桁~16桁の英数字）</li>
                      </ul>
                    </div>

                    <div className="notice-box">
                      <h4 className="font-semibold mb-2">注意事項</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>電子証明書が有効期限内であることをご確認ください</li>
                        <li>パスワードを3回連続で間違えるとロックされます</li>
                      </ul>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <div className="guide-section">
            <h2 className="text-lg font-semibold mb-4">重要な注意事項</h2>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• 法人確認と本人確認は、両方とも完了する必要があります</li>
              <li>• 提出された書類や情報に不備がある場合は、確認に時間がかかる場合があります</li>
              <li>• 確認完了までは、一部のサービス機能が制限される場合があります</li>
              <li>• 虚偽の情報を提出した場合、法的措置の対象となる可能性があります</li>
              <li>• 取得した個人情報は、本人確認の目的以外には使用いたしません</li>
            </ul>
          </div>
          <div className="flex justify-center space-x-4 mt-8">
            <button
              type="button"
              id="nextButton"
              className={`px-6 py-2 ${isPersonalVerificationDisabled ? 'bg-gray-400' : 'bg-gray-900'} text-white rounded-md`}
              onClick={() => {
                setIsLoading(true);
                if(!isLoading) {
                  if(!isSubmitted)
                    handleVerification();
                  else
                    trustdock.open();
                }
              }}
              disabled={isPersonalVerificationDisabled}
            >
              {isLoading ? '読み込み中...' : isSubmitted ? '次へ' : '確認方法を選択してください'}
            </button>
          </div>
        </main>
      </div>
    </>
  );
};

export default LegalPersonalEntryFormGuide;
